import { PromptDTO, SavedProposedTacticsDTO } from "@shaarp/common";
import { createContext, useCallback, useContext } from "react";

/** The cache key prefix of a thread's saved-proposal lookup. */
export const SAVED_PROPOSED_TACTICS_QUERY_KEY = "prompts-from-conversation";

export type SavedProposedTactics = {
  /** Every call answered so far: its saved tactic, or null. */
  readonly prompts: SavedProposedTacticsDTO["prompts"];
  readonly isFetching: boolean;
  /** The calls a later proposal of the thread revised. */
  readonly revised: ReadonlySet<string>;
  /**
   * Records a save. A revision's save moves the tactic off the calls it
   * replaces, so those read as unsaved from then on.
   */
  readonly markSaved: (
    toolCallId: string,
    prompt: PromptDTO,
    replaced?: readonly string[],
  ) => void;
};

/** Filled by `SavedProposedTacticsProvider`, around a chat thread. */
export const SavedProposedTacticsContext =
  createContext<SavedProposedTactics | null>(null);

/**
 * What the thread knows about one proposal: the tactic saved from it, whether
 * the answer is still on its way, whether a later proposal revised it, and how
 * to record a save made from its card.
 *
 * Outside a thread (no provider), nothing is known and nothing is pending: the
 * card shows its form, whose save returns the tactic already saved, not a copy.
 */
export function useSavedProposedTactic(toolCallId: string): {
  saved: PromptDTO | null;
  isLookingUp: boolean;
  isRevised: boolean;
  markSaved: (prompt: PromptDTO, replaced?: readonly string[]) => void;
} {
  const context = useContext(SavedProposedTacticsContext);
  const markSaved = context?.markSaved;
  const recordSave = useCallback(
    (prompt: PromptDTO, replaced?: readonly string[]) =>
      markSaved?.(toolCallId, prompt, replaced),
    [markSaved, toolCallId],
  );
  if (!context) {
    return {
      saved: null,
      isLookingUp: false,
      isRevised: false,
      markSaved: recordSave,
    };
  }
  const isAnswered = toolCallId in context.prompts;
  return {
    saved: context.prompts[toolCallId] ?? null,
    isLookingUp: !isAnswered && context.isFetching,
    isRevised: context.revised.has(toolCallId),
    markSaved: recordSave,
  };
}
